// One-click ownership confirmation shown between Intake and the carousel.
// Soft verification only — never blocks the flow. Captures relationship for
// future hard verification (doc upload at MLS gate).

import { useEffect } from "react";
import Logo from "./Logo";
import { track } from "../lib/api";

// Attom returns owner data in a few possible shapes depending on the plan.
const formatOwner = (record) => {
  if (!record) return null;
  // Try common shapes:
  // 1. { owner1: { firstnameandmi, lastname }, owner2: {...} }
  const o1 = record.owner1 || record.owner || record;
  const parts = [];
  if (o1?.firstnameandmi) parts.push(o1.firstnameandmi);
  if (o1?.lastname) parts.push(o1.lastname);
  if (parts.length) return parts.join(" ").replace(/\s+/g, " ").trim();
  // 2. Pre-joined name field
  if (typeof o1?.fullname === "string") return o1.fullname.trim();
  if (typeof o1?.name === "string") return o1.name.trim();
  return null;
};

export default function OwnershipConfirm({ journey, onConfirm, onCancel, isDemo }) {
  const record = journey.property?.property?.[0]?.owner_record;
  const ownerName = formatOwner(record);

  // Demo mode: auto-confirm after a brief pause so the screen is visible.
  useEffect(() => {
    if (!isDemo) return;
    const t = setTimeout(() => pick("self"), 2500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  const pick = (relationship) => {
    track("seller_ownership_confirm", { relationship, has_owner_record: Boolean(ownerName) });
    onConfirm({
      owner_of_record: ownerName,
      is_self: relationship === "self",
      relationship,
      confirmed_at: new Date().toISOString(),
    });
  };

  return (
    <div className="s-app">
      <header className="s-top">
        <div className="s-brand" onClick={onCancel}>
          <Logo />
          <div className="s-brand-name">6zero.ai</div>
        </div>
        <button type="button" className="s-back ml-auto" onClick={onCancel}>← back</button>
      </header>

      <div className="s-confirm-wrap">
        <div className="s-confirm">
          <div className="s-confirm-pill">
            <span>Quick check · we'll only ask once</span>
          </div>
          <h1>Just confirming this is your property.</h1>
          <div className="s-confirm-addr">{journey.address}</div>

          <div className="s-confirm-owner">
            <div className="s-confirm-owner-l">Owner on public record</div>
            {ownerName
              ? <div className="s-confirm-owner-v">{ownerName}</div>
              : <div className="s-confirm-owner-none">Not available — we'll verify with a document later.</div>}
          </div>

          <div className="s-confirm-actions">
            <button type="button" className="s-confirm-btn primary" onClick={() => pick("self")}>
              <span>{ownerName ? `Yes, I'm ${ownerName.split(" ")[0]}` : "Yes, that's me"}</span>
              <span className="s-confirm-btn-arrow">→</span>
            </button>
            <button type="button" className="s-confirm-btn ghost" onClick={() => pick("representing")}>
              <span>I'm representing them (POA, trustee, family)</span>
              <span className="s-confirm-btn-arrow">→</span>
            </button>
            <button type="button" className="s-confirm-btn ghost" onClick={() => pick("other")}>
              <span>Different situation — let me explain</span>
              <span className="s-confirm-btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
